import isEqual from 'lodash.isequal';
import * as React from 'react';
import { ItemSelector } from 'vet-bones/components';

import { createNewSelectItemRenderer } from 'src/components/select/createNewSelectItemRenderer';
import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_RELATIONSHIP_TYPE,
  TEST_IDS,
  getProjectDiagramEntityDrawerSelectItemTestId,
  getProjectDiagramEntityDrawerSelectorTestId,
} from 'src/constants/testIds';
import { ProjectDiagramClassDrawerRelationshipsDirections } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationshipsDirections';
import { ClassRelationshipItem } from 'src/containers/ProjectDiagram/components/classDrawer/types';
import { AddButton } from 'src/containers/ProjectDiagram/components/ProjectDiagramEntityDrawerButtons';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { useCreateModelConceptRelationshipFromQuery } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useCreateModelConceptRelationshipFromQuery';
import { projectRelationshipItemsSelector } from 'src/selectors/projectRelationshipItems';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectDiagramClassDrawerRelationshipsAddRowProps = {
  additionalIris?: Set<string>;
  currentRelationships: ClassRelationshipItem[];
  onSubmit: (item: ClassRelationshipItem) => void;
};

export const ProjectDiagramClassDrawerRelationshipsAddRow: React.VFC<
  ProjectDiagramClassDrawerRelationshipsAddRowProps
> = ({ additionalIris, currentRelationships, onSubmit }) => {
  const { projectId } = useRouterParams();

  const allRelationships = useReduxSelector(
    projectRelationshipItemsSelector(projectId),
    isEqual
  );

  const currentRelationshipIris = React.useMemo(
    () =>
      new Set(currentRelationships.map((relationship) => relationship.value)),
    [currentRelationships]
  );

  const otherRelationships = allRelationships.filter(
    (rel) => !currentRelationships.find((curr) => curr.value === rel.value)
  );

  const [relationship, setRelationship] =
    React.useState<ClassRelationshipItem | null>(null);
  const [localDirections, setLocalDirections] = React.useState<
    RelationshipDirection[]
  >([]);

  const combinedIris = React.useMemo(() => {
    const all = new Set<string>();
    currentRelationshipIris.forEach((iri) => all.add(iri));
    additionalIris?.forEach((iri) => all.add(iri));
    return all;
  }, [currentRelationshipIris, additionalIris]);

  const createNewRelationshipFromQuery =
    useCreateModelConceptRelationshipFromQuery(combinedIris);

  const handleSelectRelationship = (selected: ClassRelationshipItem | null) => {
    if (!selected || (selected.isNewlyCreated && !selected.text)) {
      setRelationship(null);
    } else {
      setRelationship(selected);
    }
  };

  let tooltipContent = '';
  if (!relationship) {
    tooltipContent =
      copy.components.projectDiagram.classDrawer.relationshipsTable
        .missingRelationship;
  } else if (!localDirections.length) {
    tooltipContent =
      copy.components.projectDiagram.classDrawer.relationshipsTable
        .missingDirection;
  }
  const disabled = Boolean(tooltipContent);

  const handleSubmit = () => {
    if (disabled || !relationship) {
      return;
    }

    onSubmit({
      ...relationship,
      directions: localDirections,
    });

    setRelationship(null);
    setLocalDirections([]);
  };

  return (
    <tr data-testid={TEST_IDS.projectDiagram.entityDrawer.tableAddRow}>
      <td>
        <ItemSelector.Single
          buttonText={relationship?.text || ''}
          creatable
          createNewItemFromQuery={createNewRelationshipFromQuery}
          createNewItemRenderer={createNewSelectItemRenderer}
          createNewItemTestId={getProjectDiagramEntityDrawerSelectItemTestId(
            ''
          )}
          filterInputProps={
            {
              'data-testid':
                TEST_IDS.projectDiagram.entityDrawer.selectorFilter,
              placeholder: copy.components.defaults.filterOrCreate,
            } as any
          }
          items={otherRelationships}
          itemTestId={getProjectDiagramEntityDrawerSelectItemTestId}
          onSelect={handleSelectRelationship}
          resetOnClose
          selected={relationship}
          testId={getProjectDiagramEntityDrawerSelectorTestId(
            PROJECT_DIAGRAM_ENTITY_DRAWER_RELATIONSHIP_TYPE
          )}
        />
      </td>
      <td>
        <ProjectDiagramClassDrawerRelationshipsDirections
          directions={localDirections}
          setDirections={setLocalDirections}
        />
      </td>
      <td>
        <AddButton
          disabled={disabled}
          onClick={handleSubmit}
          tooltip={tooltipContent}
        />
      </td>
    </tr>
  );
};
